import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { useActivityStore } from '@/store/activityStore';
import { useActivityTypeStore } from '@/store/activityTypeStore';
import { useAuthStore } from '@/store/authStore';
import { usePracticeStore } from '@/store/practiceStore';
import { apiClient } from '@/utils/api';
import { toast } from 'sonner';
import { DateTimePickerItalian } from '@/components/ui/datetime-picker-italian';

// Importazione necessaria per accedere allo store al di fuori di un componente
const usePracticeStoreGetState = usePracticeStore.getState;

type Priority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

interface CreateActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  practiceId: string;
  clientName?: string; // Nome del cliente per auto-compilazione titolo
  interventionLocation?: string; // Luogo intervento per auto-compilazione titolo (preferito su clientAddress)
  clientAddress?: string; // Fallback: indirizzo cliente (deprecato)
  onSuccess?: () => void;
}

export default function CreateActivityDialog({
  open,
  onOpenChange,
  practiceId,
  clientName,
  interventionLocation,
  clientAddress,
  onSuccess,
}: CreateActivityDialogProps) {
  const [formData, setFormData] = useState({
    userId: '',
    type: '',
    title: '',
    description: '',
    dueDate: '',
    priority: 'MEDIUM' as Priority,
  });
  const [titleManuallyEdited, setTitleManuallyEdited] = useState(false);

  const { createActivity, loading } = useActivityStore();
  const { activityTypes, fetchActivityTypes } = useActivityTypeStore();
  const { user: currentUser } = useAuthStore();
  const { fetchPractice } = usePracticeStore();
  const [users, setUsers] = useState<Array<{ id: string; firstName: string; lastName: string; email: string }>>([]);
  const [practiceTitle, setPracticeTitle] = useState<string>('');

  // Formatta datetime-local per input HTML
  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  useEffect(() => {
    if (open) {
      // Fetch activity types dal database
      fetchActivityTypes(true);
      
      // Fetch users (endpoint accessibile a tutti gli utenti autenticati)
      apiClient.get('/users/assignable')
        .then((response) => {
          console.log('Users API response:', response.data);
          const usersData = response.data?.data || [];
          console.log('Parsed users:', usersData);
          setUsers(usersData);
        })
        .catch((error) => {
          console.error('Error fetching users:', error);
          // Se fallisce, usa almeno l'utente corrente
          if (currentUser) {
            setUsers([{
              id: currentUser.id,
              firstName: currentUser.firstName || '',
              lastName: currentUser.lastName || '',
              email: currentUser.email || '',
            }]);
          }
        });
      
      // Fetch pratica per ottenere il titolo
      if (practiceId) {
        fetchPractice(practiceId)
          .then(() => {
            // La pratica viene salvata nello store, leggiamola da lì
            setTimeout(() => {
              const practice = usePracticeStoreGetState().currentPractice;
              if (practice) {
                setPracticeTitle(practice.title || '');
              }
            }, 100);
          })
          .catch((error) => {
            console.error('Error fetching practice:', error);
            setPracticeTitle('');
          });
      }
      
      // Reset form quando si apre
      const now = new Date();
      setFormData({
        userId: '', // Non predefinito, deve essere "da Definire"
        type: '',
        title: '',
        description: '',
        dueDate: formatDateTimeLocal(now),
        priority: 'MEDIUM',
      });
      setTitleManuallyEdited(false);
      setPracticeTitle('');
    }
  }, [open, currentUser, practiceId, fetchPractice, fetchActivityTypes]);

  // Auto-compila titolo quando cambia tipo attività
  // Formato: Tipo - NomeCliente - TitoloPraticaPulito - Indirizzo
  useEffect(() => {
    if (formData.type && !titleManuallyEdited) {
      const typeLabel = activityTypes.find(t => t.code === formData.type)?.name || formData.type;
      let autoTitle = typeLabel;
      
      // Aggiungi nome cliente se presente
      if (clientName) {
        autoTitle = `${autoTitle} - ${clientName}`;
      }
      
      // Aggiungi titolo pratica se presente, rimuovendo il nome cliente se presente
      if (practiceTitle) {
        let cleanPracticeTitle = practiceTitle;
        // Rimuovi il nome cliente dal titolo pratica se presente
        if (clientName && practiceTitle.includes(clientName)) {
          // Rimuovi il nome cliente e eventuali separatori
          cleanPracticeTitle = practiceTitle
            .replace(new RegExp(`\\s*-\\s*${clientName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi'), '')
            .replace(new RegExp(`${clientName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*-\\s*`, 'gi'), '')
            .replace(new RegExp(`\\s*${clientName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`, 'gi'), '')
            .trim();
          // Rimuovi eventuali doppi separatori o separatori iniziali/finali
          cleanPracticeTitle = cleanPracticeTitle.replace(/\s*-\s*-\s*/g, ' - ').replace(/^\s*-\s*|\s*-\s*$/g, '').trim();
        }
        if (cleanPracticeTitle) {
          autoTitle = `${autoTitle} - ${cleanPracticeTitle}`;
        }
      }
      
      // Aggiungi luogo intervento o indirizzo se presente (preferenza a interventionLocation)
      const location = interventionLocation || clientAddress;
      if (location) {
        autoTitle = `${autoTitle} - ${location}`;
      }
      
      setFormData(prev => ({ ...prev, title: autoTitle }));
    }
  }, [formData.type, clientName, interventionLocation, clientAddress, practiceTitle, titleManuallyEdited]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.type || !formData.title) {
      toast.error('Tipo e titolo sono obbligatori');
      return;
    }

    // Validazione data retroattiva
    if (formData.dueDate) {
      const dueDate = new Date(formData.dueDate);
      const now = new Date();
      if (dueDate < now) {
        toast.error('La data di scadenza non può essere nel passato');
        return;
      }
    }

    try {
      // Converti datetime-local in ISO string per il backend
      const dueDateISO = formData.dueDate ? new Date(formData.dueDate).toISOString() : undefined;
      
      await createActivity({
        practiceId,
        userId: formData.userId || undefined,
        type: formData.type,
        title: formData.title,
        description: formData.description || undefined,
        dueDate: dueDateISO,
        priority: formData.priority,
      });

      toast.success('Attività creata con successo');
      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Errore nella creazione dell\'attività');
    }
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuova Attività</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="type">Tipo Attività *</Label>
            <Combobox
              autoFocusTrigger={open}
              options={activityTypes
                .filter(t => t.isActive)
                .sort((a, b) => a.order - b.order)
                .map((type) => ({
                  value: type.code,
                  label: `${type.icon || ''} ${type.name}`.trim(),
                }))}
              value={formData.type}
              onValueChange={(value) => setFormData({ ...formData, type: value })}
              placeholder="Seleziona tipo attività"
              searchPlaceholder="Cerca tipo attività..."
              emptyMessage="Nessun tipo attività trovato"
            />
          </div>

          <div>
            <Label htmlFor="title">Titolo *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                setTitleManuallyEdited(true);
              }}
              placeholder="Es: Chiamata cliente per aggiornamento"
              required
            />
          </div>

          <div>
            <Label htmlFor="description">Descrizione</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descrizione dettagliata dell'attività..."
              rows={4}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="dueDate">Scadenza</Label>
              <DateTimePickerItalian
                id="dueDate"
                value={formData.dueDate}
                onChange={(value) => setFormData({ ...formData, dueDate: value })}
                placeholder="GG MM AAAA HH:mm"
                min={new Date()}
              />
            </div>

            <div>
              <Label htmlFor="priority">Priorità</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value as Priority })}
              >
                <SelectTrigger id="priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOW">Bassa</SelectItem>
                  <SelectItem value="MEDIUM">Media</SelectItem>
                  <SelectItem value="HIGH">Alta</SelectItem>
                  <SelectItem value="URGENT">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="userId">Assegnatario</Label>
            <Combobox
              options={users.map(user => ({
                value: user.id,
                label: `${user.firstName} ${user.lastName}`,
                description: user.email || undefined,
              }))}
              value={formData.userId}
              onValueChange={(value) => setFormData({ ...formData, userId: value })}
              placeholder="da Definire"
              searchPlaceholder="Cerca utente..."
              emptyMessage="Nessun utente trovato"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Annulla
            </Button>
            <Button type="submit" disabled={loading || !formData.type || !formData.title}>
              {loading ? 'Creazione...' : 'Crea Attività'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

